import { useIntl } from 'react-intl';
import { siteIntelligenceServiceLabel } from '../../../../mappings';
import SummaryTable, { SummaryRow } from '../../table/SummaryTable';
import { valueHandler } from '../../../../../../../_metronic/helpers';
import Time from '../../../../../../modules/components/time/Time';

interface Props {
  data: any;
  status: string;
  refetch: () => void;
}

export default function Detail({ data, status, refetch }: Props) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `SI_DOMAIN.${id}` });
  const emDash = '—';

  const filterObjectFields = <T extends Record<string, any>>(
    obj: T,
    keyString: string
  ): Partial<T> => {
    if (!obj) return undefined;
    const result: Partial<T> = {};

    for (const [key, value] of Object.entries(obj)) {
      if (key.includes(keyString)) {
        result[key as keyof T] = typeof value === 'string' ? value.split(' ')[0] : value;
      } else if (!key.includes(keyString)) {
        result[key as keyof T] = value;
      }
    }

    return result;
  };

  const websiteInfo: SummaryRow[] = [
    // ...formatSummaryRowByKey(filterObjectFields(data?.result?.domain_info, 'email')),
    {
      key: 'website',
      field: t('WEBSITE'),
      render: () => valueHandler(data?.result?.domain_info?.website),
    },
    {
      key: 'title',
      field: t('TITLE'),
      render: () => valueHandler(data?.result?.domain_info?.title),
    },
    {
      key: 'description',
      field: t('DESCRIPTION'),
      render: () => valueHandler(data?.result?.domain_info?.description),
    },
    {
      key: 'keywords',
      field: t('KEYWORDS'),
      render: () => valueHandler(data?.result?.domain_info?.keywords),
    },
    {
      key: 'site_found',
      field: t('SITE_AVAILABILITY'),
      render: () => (data?.result?.domain_info?.site_found ? t('AVAILABLE') : t('NOT_FOUND')),
    },
  ];

  const domainInfo: SummaryRow[] = [
    {
      key: 'domain',
      field: t('DOMAIN'),
      render: () => valueHandler(data?.result?.domain_info?.domain),
    },
    {
      key: 'ip',
      field: t('IP'),
      render: () => (
        <>
          {valueHandler(data?.result?.domain_info?.ip)}{' '}
          {data?.result?.domain_info?.country && `(${data?.result?.domain_info?.country})`}
        </>
      ),
    },
    {
      key: 'owner',
      field: t('DOMAIN_OWNER'),
      render: () => data?.result?.domain_info?.owner,
    },
    {
      key: 'domain_age',
      field: t('DOMAIN_AGE'),
      render: () =>
        data?.result?.domain_info?.age &&
        Object.keys(data?.result?.domain_info?.age).length > 0 && (
          <span>
            {!!data?.result?.domain_info?.age?.years &&
              `${data?.result?.domain_info?.age?.years} ${t('YEARS')} `}
            {!!data?.result?.domain_info?.age?.months &&
              `${data?.result?.domain_info?.age?.months} ${t('MONTHS')} `}
            {!!data?.result?.domain_info?.age?.days &&
              `${data?.result?.domain_info?.age?.days} ${t('DAYS')} `}
          </span>
        ),
    },
    {
      key: 'creation_date',
      field: t('CREATE_DATE'),
      render: () =>
        data?.result?.domain_info?.creation_date && (
          <Time
            date={new Date(data?.result?.domain_info?.creation_date)}
            format='yyyy-MM-dd HH:mm:ss'
            utc
          />
        ),
    },
    {
      key: 'expiry_date',
      field: t('EXPIRY_DATE'),
      render: () =>
        data?.result?.domain_info?.expiry_date && (
          <Time
            date={new Date(data?.result?.domain_info?.expiry_date)}
            format='yyyy-MM-dd HH:mm:ss'
            utc
          />
        ),
    },
    {
      key: 'updated_date',
      field: t('UPDATE_DATE'),
      render: () =>
        data?.result?.domain_info?.updated_date && (
          <Time
            date={new Date(data?.result?.domain_info?.updated_date)}
            format='yyyy-MM-dd HH:mm:ss'
            utc
          />
        ),
    },
    {
      key: 'registrar_abuse_contact_email',
      field: t('REGISTRAR_ABUSE_CONTACT_EMAIL'),
      render: () =>
        data?.result?.domain_info?.registrar_abuse_contact_email?.includes(' ')
          ? null
          : data?.result?.domain_info?.registrar_abuse_contact_email,
    },
    {
      key: 'registrant_email',
      field: t('REGISTRANT_EMAIL'),
      render: () =>
        data?.result?.domain_info?.registrant_email?.includes(' ')
          ? null
          : data?.result?.domain_info?.registrant_email,
    },
    {
      key: 'registrar',
      field: t('REGISTRAR'),
      render: () => data?.result?.domain_info?.registrar,
    },
  ]?.filter((item) => item.render());

  return (
    <div className='card mb-5 mb-xl-10 border'>
      <div className='card-header align-items-center justify-content-start'>
        <h4 className='my-6'>{t('TITLE')}</h4>
      </div>
      <div className='card-body p-9 d-flex flex-column gap-9'>
        <div>
          <h5 className='fw-bolder m-0'>{t('WEBSITE_INFO')}</h5>
          <SummaryTable rows={websiteInfo} data={data} refetch={refetch} />
        </div>
        <div>
          <h5 className='fw-bolder m-0'>{t('DOMAIN_INFO')}</h5>
          <SummaryTable rows={domainInfo} data={data} refetch={refetch} className='mb-0' />
        </div>
        <div>
          <a className='text-primary fs-5 fw-bolder' href='#whois-content'>
            {t('DETAILS_WHOIS_RECORD')}
          </a>
        </div>
      </div>
      <div className='d-flex justify-content-end mb-5'>
        <a href='#summary' className='px-8 d-none print-display-button'>
          <i className="bi bi-caret-up-square-fill fs-3"></i>
        </a>
      </div>
    </div>
  );
}
